import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ClaudeTaskCommand } from '@/types/claudeTask';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Camera, GitCompare, Calendar, Zap, RefreshCw, FileSearch, Wrench, PlusCircle, FilePlus, Tag, Globe, Search, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useFetchAppTags } from '@/hooks/useAppTags';
import { useAvailablePeriods } from '@/hooks/useClaudeTasks';
import { BulkTagPagesConfirmDialog } from './BulkTagPagesConfirmDialog';
import { BulkScrapeConfirmDialog } from './BulkScrapeConfirmDialog';
import { BulkExtractConfirmDialog } from './BulkExtractConfirmDialog';
import { BulkDiffConfirmDialog } from './BulkDiffConfirmDialog';

const PRICINGSAAS_API_KEY = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY;

interface CreateTaskDialogProps {
  onTaskCreated?: () => void;
}

// Helper to get current ISO week number
function getISOWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

// Helper to get current week period string (e.g., "2025W48")
function getCurrentWeekPeriod(): string {
  const now = new Date();
  const year = now.getFullYear();
  const week = getISOWeek(now);
  return `${year}W${week.toString().padStart(2, '0')}`;
}

// Helper to get current quarter period string (e.g., "2025Q4")
function getCurrentQuarterPeriod(): string {
  const now = new Date();
  const year = now.getFullYear();
  const quarter = Math.ceil((now.getMonth() + 1) / 3);
  return `${year}Q${quarter}`;
}

// Get recent quarters for dropdown (from 2023Q2 to current quarter)
function getRecentQuarters(): string[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);
  const quarters: string[] = [];

  // Start from 2023Q2 and go to current quarter
  const startYear = 2023;
  const startQuarter = 2;

  let year = currentYear;
  let quarter = currentQuarter;

  // Generate quarters backwards from current to 2023Q2
  while (year > startYear || (year === startYear && quarter >= startQuarter)) {
    quarters.push(`${year}Q${quarter}`);
    quarter--;
    if (quarter === 0) {
      quarter = 4;
      year--;
    }
  }

  return quarters;
}

// Get recent years for dropdown (from 2024 to current year)
function getRecentYears(): string[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const years: string[] = [];

  // Start from current year and go back to 2024
  for (let year = currentYear; year >= 2024; year--) {
    years.push(`${year}Y`);
  }

  return years;
}

// Get recent weeks for dropdown
function getRecentWeeks(): string[] {
  const now = new Date();
  const weeks: string[] = [];

  for (let i = 0; i < 4; i++) {
    const date = new Date(now);
    date.setDate(date.getDate() - (i * 7));
    const year = date.getFullYear();
    const week = getISOWeek(date);
    weeks.push(`${year}W${week.toString().padStart(2, '0')}`);
  }

  return weeks;
}

type TaskTemplate = 'scrape' | 'run_diff' | 'run_extract' | 'fix_diff' | 'run_add' | 'run_add_page' | 'tag_page' | 'custom';

export function CreateTaskDialog({ onTaskCreated }: CreateTaskDialogProps) {
  const [open, setOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(null);
  const [slug, setSlug] = useState('');
  const [slugSearchResults, setSlugSearchResults] = useState<any[]>([]);
  const [isSearchingSlug, setIsSearchingSlug] = useState(false);
  const [showSlugDropdown, setShowSlugDropdown] = useState(false);

  const [period, setPeriod] = useState(getCurrentWeekPeriod());
  const [periodType, setPeriodType] = useState<'week' | 'quarter' | 'year' | 'custom'>('week');
  const [customPeriod, setCustomPeriod] = useState('');
  const [force, setForce] = useState(false);
  const [priority, setPriority] = useState(5); // Default priority is 5
  const [prompt, setPrompt] = useState(''); // For fix_diff tasks
  const [pricingPageUrl, setPricingPageUrl] = useState(''); // For run_add tasks
  const [companySlug, setCompanySlug] = useState(''); // For run_add tasks (optional)
  const [pageSlug, setPageSlug] = useState(''); // For run_add tasks (optional)
  const [navigationHint, setNavigationHint] = useState(''); // For run_add tasks (optional)
  const [selectedTag, setSelectedTag] = useState(''); // For tag_page tasks
  const [bulkAllPages, setBulkAllPages] = useState(false);
  const [fetchedPages, setFetchedPages] = useState<string[]>([]);
  const [isFetchingPages, setIsFetchingPages] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [bulkAllPagesScrape, setBulkAllPagesScrape] = useState(false);
  const [fetchedPagesScrape, setFetchedPagesScrape] = useState<string[]>([]);
  const [isFetchingPagesScrape, setIsFetchingPagesScrape] = useState(false);
  const [showConfirmDialogScrape, setShowConfirmDialogScrape] = useState(false);
  const [bulkAllPagesExtract, setBulkAllPagesExtract] = useState(false);
  const [fetchedPagesExtract, setFetchedPagesExtract] = useState<string[]>([]);
  const [isFetchingPagesExtract, setIsFetchingPagesExtract] = useState(false);
  const [showConfirmDialogExtract, setShowConfirmDialogExtract] = useState(false);
  const [bulkAllPagesDiff, setBulkAllPagesDiff] = useState(false);
  const [fetchedPagesDiff, setFetchedPagesDiff] = useState<string[]>([]);
  const [isFetchingPagesDiff, setIsFetchingPagesDiff] = useState(false);
  const [showConfirmDialogDiff, setShowConfirmDialogDiff] = useState(false);
  const [vault, setVault] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Fetch tags and periods for tag_page task
  const { data: appTags } = useFetchAppTags();
  const { data: availablePeriods } = useAvailablePeriods();

  const recentQuarters = useMemo(() => getRecentQuarters(), []);
  const recentWeeks = useMemo(() => getRecentWeeks(), []);
  const recentYears = useMemo(() => getRecentYears(), []);

  const resetForm = () => {
    setSelectedTemplate(null);
    setSlug('');
    setPeriod(getCurrentWeekPeriod());
    setPeriodType('week');
    setCustomPeriod('');
    setForce(false);
    setPriority(5);
    setPrompt('');
    setPricingPageUrl('');
    setCompanySlug('');
    setPageSlug('');
    setNavigationHint('');
    setSelectedTag('');
    setBulkAllPages(false);
    setFetchedPages([]);
    setShowConfirmDialog(false);
    setBulkAllPagesScrape(false);
    setFetchedPagesScrape([]);
    setShowConfirmDialogScrape(false);
    setBulkAllPagesExtract(false);
    setFetchedPagesExtract([]);
    setShowConfirmDialogExtract(false);
    setBulkAllPagesDiff(false);
    setFetchedPagesDiff([]);
    setShowConfirmDialogDiff(false);
    setVault(false);
  };

  const fetchAllPages = async () => {
    setIsFetchingPages(true);
    try {
      const response = await fetch('https://api.pricingsaas.com/functions/v1/api/pages/slugs');

      if (!response.ok) {
        throw new Error(`Failed to fetch page slugs: ${response.status} ${response.statusText}`);
      }

      const slugs: string[] = await response.json();

      if (slugs.length === 0) {
        toast({
          title: 'No pages found',
          description: 'No pages available in the system',
          variant: 'destructive',
        });
        setIsFetchingPages(false);
        return;
      }

      setFetchedPages(slugs);
      setShowConfirmDialog(true);
    } catch (error) {
      console.error('[fetchAllPages] Error:', error);
      toast({
        title: 'Error fetching pages',
        description: error instanceof Error ? error.message : 'Failed to fetch pages',
        variant: 'destructive',
      });
    } finally {
      setIsFetchingPages(false);
    }
  };

  const fetchAllPagesForScrape = async () => {
    setIsFetchingPagesScrape(true);
    try {
      const response = await fetch('https://api.pricingsaas.com/functions/v1/api/pages/slugs');

      if (!response.ok) {
        throw new Error(`Failed to fetch page slugs: ${response.status} ${response.statusText}`);
      }

      const slugs: string[] = await response.json();

      if (slugs.length === 0) {
        toast({
          title: 'No pages found',
          description: 'No pages available in the system',
          variant: 'destructive',
        });
        setIsFetchingPagesScrape(false);
        return;
      }

      setFetchedPagesScrape(slugs);
      setShowConfirmDialogScrape(true);
    } catch (error) {
      console.error('[fetchAllPagesForScrape] Error:', error);
      toast({
        title: 'Error fetching pages',
        description: error instanceof Error ? error.message : 'Failed to fetch pages',
        variant: 'destructive',
      });
    } finally {
      setIsFetchingPagesScrape(false);
    }
  };

  const fetchAllPagesForExtract = async () => {
    setIsFetchingPagesExtract(true);
    try {
      const response = await fetch('https://api.pricingsaas.com/functions/v1/api/pages/slugs');

      if (!response.ok) {
        throw new Error(`Failed to fetch page slugs: ${response.status} ${response.statusText}`);
      }

      const slugs: string[] = await response.json();

      if (slugs.length === 0) {
        toast({
          title: 'No pages found',
          description: 'No pages available in the system',
          variant: 'destructive',
        });
        setIsFetchingPagesExtract(false);
        return;
      }

      setFetchedPagesExtract(slugs);
      setShowConfirmDialogExtract(true);
    } catch (error) {
      console.error('[fetchAllPagesForExtract] Error:', error);
      toast({
        title: 'Error fetching pages',
        description: error instanceof Error ? error.message : 'Failed to fetch pages',
        variant: 'destructive',
      });
    } finally {
      setIsFetchingPagesExtract(false);
    }
  };

  const fetchAllPagesForDiff = async () => {
    setIsFetchingPagesDiff(true);
    try {
      const response = await fetch('https://api.pricingsaas.com/functions/v1/api/pages/slugs');

      if (!response.ok) {
        throw new Error(`Failed to fetch page slugs: ${response.status} ${response.statusText}`);
      }

      const slugs: string[] = await response.json();

      if (slugs.length === 0) {
        toast({
          title: 'No pages found',
          description: 'No pages available in the system',
          variant: 'destructive',
        });
        setIsFetchingPagesDiff(false);
        return;
      }

      setFetchedPagesDiff(slugs);
      setShowConfirmDialogDiff(true);
    } catch (error) {
      console.error('[fetchAllPagesForDiff] Error:', error);
      toast({
        title: 'Error fetching pages',
        description: error instanceof Error ? error.message : 'Failed to fetch pages',
        variant: 'destructive',
      });
    } finally {
      setIsFetchingPagesDiff(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate based on template type
    if (!selectedTemplate) return;
    if (selectedTemplate === 'run_add') {
      if (!pricingPageUrl) return;
    } else if (selectedTemplate === 'run_add_page') {
      if (!slug || !pageSlug || !pricingPageUrl) return;
    } else if (selectedTemplate === 'tag_page') {
      // For tag_page, slug is optional if bulk mode is enabled
      if (!bulkAllPages && !slug) return;
      if (!selectedTag) return;
    } else if (selectedTemplate === 'scrape') {
      // For scrape, slug is optional if bulk mode is enabled
      if (!bulkAllPagesScrape && !slug) return;
    } else if (selectedTemplate === 'run_extract') {
      // For run_extract, slug is optional if bulk mode is enabled
      if (!bulkAllPagesExtract && !slug) return;
    } else if (selectedTemplate === 'run_diff') {
      // For run_diff, slug is optional if bulk mode is enabled
      if (!bulkAllPagesDiff && !slug) return;
    } else {
      if (!slug) return;
    }

    setIsSubmitting(true);

    try {
      let command: string;
      let params: Record<string, any> = {};
      let taskSlug: string;

      // Add force flag if enabled
      if (force) {
        params.force = true;
      }

      if (selectedTemplate === 'scrape') {
        if (bulkAllPagesScrape) {
          // Show preview dialog instead of creating task
          await fetchAllPagesForScrape();
          setIsSubmitting(false);
          return;
        }
        command = ClaudeTaskCommand.SCRAPE;
        params.slug = slug;
        taskSlug = slug;
      } else if (selectedTemplate === 'run_diff') {
        if (bulkAllPagesDiff) {
          // Show preview dialog instead of creating task
          await fetchAllPagesForDiff();
          setIsSubmitting(false);
          return;
        }
        command = ClaudeTaskCommand.RUN_DIFF;
        params.slug = slug;
        taskSlug = slug;
        const effectivePeriod = periodType === 'custom' ? customPeriod : period;
        params.period = effectivePeriod;
        if (vault) {
          params.vault = true;
        }
      } else if (selectedTemplate === 'run_extract') {
        if (bulkAllPagesExtract) {
          // Show preview dialog instead of creating task
          await fetchAllPagesForExtract();
          setIsSubmitting(false);
          return;
        }
        command = ClaudeTaskCommand.EXTRACT;
        params.slug = slug;
        taskSlug = slug;
        const effectivePeriod = periodType === 'custom' ? customPeriod : period;
        params.period = effectivePeriod;
      } else if (selectedTemplate === 'fix_diff') {
        command = ClaudeTaskCommand.FIX_DIFF;
        params.slug = slug;
        taskSlug = slug;
        const effectivePeriod = periodType === 'custom' ? customPeriod : period;
        params.period = effectivePeriod;
        params.prompt = prompt;
      } else if (selectedTemplate === 'run_add') {
        command = ClaudeTaskCommand.RUN_ADD;
        params.pricing_page_url = pricingPageUrl;
        if (companySlug.trim()) {
          params.company_slug = companySlug.trim();
        }
        if (pageSlug.trim()) {
          params.page_slug = pageSlug.trim();
        }
        if (navigationHint.trim()) {
          params.navigation_hint = navigationHint.trim();
        }
        // Use company_slug as task slug, or derive from URL
        taskSlug = companySlug.trim() || new URL(pricingPageUrl).hostname.replace(/^www\./, '').split('.')[0];
      } else if (selectedTemplate === 'run_add_page') {
        command = ClaudeTaskCommand.RUN_ADD_PAGE;
        params.company_slug = slug;
        params.page_slug = pageSlug.trim();
        params.pricing_page_url = pricingPageUrl;
        if (navigationHint.trim()) {
          params.navigation_hint = navigationHint.trim();
        }
        taskSlug = slug;
      } else if (selectedTemplate === 'tag_page') {
        if (bulkAllPages) {
          // Show preview dialog instead of creating task
          await fetchAllPages();
          setIsSubmitting(false);
          return;
        }
        command = ClaudeTaskCommand.TAG_PAGE;
        params.slug = slug;
        taskSlug = slug;
        const effectivePeriod = periodType === 'custom' ? customPeriod : period;
        params.period = effectivePeriod;
        params.tag = selectedTag;
      } else {
        toast({
          title: 'Error',
          description: 'Invalid template selected',
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }

      const { data, error } = await supabase
        .from('claude_tasks')
        .insert({
          command,
          slug: taskSlug,
          params,
          status: 'pending',
          priority,
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      toast({
        title: 'Task created',
        description: `${command} task for "${taskSlug}" has been created`,
      });

      resetForm();
      setOpen(false);
      onTaskCreated?.();
    } catch (error) {
      console.error('Error creating task:', error);

      // Extract error message from various error types
      let errorMessage = 'An error occurred';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        // Handle Supabase error objects
        const err = error as any;
        if (err.message) {
          errorMessage = err.message;
        } else if (err.details) {
          errorMessage = err.details;
        } else if (err.error?.message) {
          errorMessage = err.error.message;
        } else if (err.data?.message) {
          errorMessage = err.data.message;
        }

        // Check for specific error codes
        if (err.code === '23505' || errorMessage.includes('duplicate')) {
          const taskType = selectedTemplate?.replace(/_/g, ' ').toUpperCase() || 'TASK';
          errorMessage = `A pending ${taskType} task already exists for this company. Please wait for it to complete or use the existing task.`;
        }
      }

      toast({
        title: 'Error creating task',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkTagPagesConfirm = async () => {
    setIsSubmitting(true);
    try {
      const effectivePeriod = periodType === 'custom' ? customPeriod : period;

      // Build all insert data in one go
      const insertData = fetchedPages.map(slug => {
        const taskParams: Record<string, any> = {
          slug: slug,
          period: effectivePeriod,
          tag: selectedTag,
        };

        if (force) {
          taskParams.force = true;
        }

        return {
          command: ClaudeTaskCommand.TAG_PAGE,
          slug: slug,
          params: taskParams,
          status: 'pending',
          priority: priority,
        };
      });

      // Single bulk insert
      const { data, error } = await supabase
        .from('claude_tasks')
        .insert(insertData);

      if (error) {
        console.error('Bulk insert error:', error);
        toast({
          title: 'Error creating bulk tasks',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Bulk tasks created',
        description: `Successfully created ${fetchedPages.length} TAG_PAGE tasks`,
      });

      resetForm();
      setShowConfirmDialog(false);
      setOpen(false);
      onTaskCreated?.();
    } catch (error) {
      console.error('Error creating bulk tasks:', error);
      toast({
        title: 'Error creating bulk tasks',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkScrapeConfirm = async () => {
    setIsSubmitting(true);
    try {
      // Build all insert data in one go
      const insertData = fetchedPagesScrape.map(slug => {
        const taskParams: Record<string, any> = {
          slug: slug,
        };

        if (force) {
          taskParams.force = true;
        }

        return {
          command: ClaudeTaskCommand.SCRAPE,
          slug: slug,
          params: taskParams,
          status: 'pending',
          priority: priority,
        };
      });

      // Single bulk insert
      const { data, error } = await supabase
        .from('claude_tasks')
        .insert(insertData);

      if (error) {
        console.error('Bulk insert error:', error);
        toast({
          title: 'Error creating bulk tasks',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Bulk tasks created',
        description: `Successfully created ${fetchedPagesScrape.length} SCRAPE tasks`,
      });

      resetForm();
      setShowConfirmDialogScrape(false);
      setOpen(false);
      onTaskCreated?.();
    } catch (error) {
      console.error('Error creating bulk tasks:', error);
      toast({
        title: 'Error creating bulk tasks',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkExtractConfirm = async () => {
    setIsSubmitting(true);
    try {
      const effectivePeriod = periodType === 'custom' ? customPeriod : period;

      // Build all insert data in one go
      const insertData = fetchedPagesExtract.map(slug => {
        const taskParams: Record<string, any> = {
          slug: slug,
          period: effectivePeriod,
        };

        if (force) {
          taskParams.force = true;
        }

        return {
          command: ClaudeTaskCommand.EXTRACT,
          slug: slug,
          params: taskParams,
          status: 'pending',
          priority: priority,
        };
      });

      // Single bulk insert
      const { data, error } = await supabase
        .from('claude_tasks')
        .insert(insertData);

      if (error) {
        console.error('Bulk insert error:', error);
        toast({
          title: 'Error creating bulk tasks',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Bulk tasks created',
        description: `Successfully created ${fetchedPagesExtract.length} EXTRACT tasks`,
      });

      resetForm();
      setShowConfirmDialogExtract(false);
      setOpen(false);
      onTaskCreated?.();
    } catch (error) {
      console.error('Error creating bulk tasks:', error);
      toast({
        title: 'Error creating bulk tasks',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkDiffConfirm = async () => {
    setIsSubmitting(true);
    try {
      const effectivePeriod = periodType === 'custom' ? customPeriod : period;

      // Build all insert data in one go
      const insertData = fetchedPagesDiff.map(slug => {
        const taskParams: Record<string, any> = {
          slug: slug,
          period: effectivePeriod,
        };

        if (force) {
          taskParams.force = true;
        }

        if (vault) {
          taskParams.vault = true;
        }

        return {
          command: ClaudeTaskCommand.RUN_DIFF,
          slug: slug,
          params: taskParams,
          status: 'pending',
          priority: priority,
        };
      });

      // Single bulk insert
      const { data, error } = await supabase
        .from('claude_tasks')
        .insert(insertData);

      if (error) {
        console.error('Bulk insert error:', error);
        toast({
          title: 'Error creating bulk tasks',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Bulk tasks created',
        description: `Successfully created ${fetchedPagesDiff.length} RUN_DIFF tasks`,
      });

      resetForm();
      setShowConfirmDialogDiff(false);
      setOpen(false);
      onTaskCreated?.();
    } catch (error) {
      console.error('Error creating bulk tasks:', error);
      toast({
        title: 'Error creating bulk tasks',
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTemplateSelect = (template: TaskTemplate) => {
    setSelectedTemplate(template);
    if (template === 'run_diff' || template === 'run_extract' || template === 'fix_diff' || template === 'tag_page') {
      setPeriod(getCurrentWeekPeriod());
      setPeriodType('week');
    }
    // Set priority to 1 for fix_diff tasks (high priority)
    if (template === 'fix_diff') {
      setPriority(1);
    } else {
      setPriority(5); // Reset to default for other templates
    }
  };

  const handleSlugSearch = async (searchQuery: string) => {
    setSlug(searchQuery);
    setIsSearchingSlug(true);
    setShowSlugDropdown(true);

    try {
      const q = searchQuery.trim();
      const url = `https://api.pricingsaas.com/companies/find${q ? `?q=${encodeURIComponent(q)}` : ''}`;
      const response = await fetch(url, {
        headers: { 'X-API-Key': PRICINGSAAS_API_KEY }
      });
      if (!response.ok) {
        setSlugSearchResults([]);
        return;
      }
      const data = await response.json();
      const results = (data.results || []).map((r: any) => ({
        id: r.company_id,
        slug: r.company_slug,
        category: null,
        logo_url: r.logo_url || null,
      }));
      setSlugSearchResults(results);
    } catch {
      setSlugSearchResults([]);
    } finally {
      setIsSearchingSlug(false);
    }
  };

  const handleSlugSelect = (selectedSlug: string) => {
    setSlug(selectedSlug);
    setShowSlugDropdown(false);
    setSlugSearchResults([]);
  };

  const handlePeriodTypeChange = (type: 'week' | 'quarter' | 'year' | 'custom') => {
    setPeriodType(type);
    if (type === 'week') {
      setPeriod(getCurrentWeekPeriod());
    } else if (type === 'quarter') {
      setPeriod(getCurrentQuarterPeriod());
    } else if (type === 'year') {
      const currentYear = new Date().getFullYear();
      setPeriod(`${currentYear}Y`);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) resetForm();
      }}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Create Task
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Create New Task</DialogTitle>
            <DialogDescription>
              Select a task template and configure the parameters.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4 px-1">
            <div className="grid gap-5">
              {/* Template Selection */}
              <div className="grid gap-2">
                <Label>Task Type</Label>
                <div className="grid grid-cols-7 gap-2">
                  <button
                    type="button"
                    onClick={() => handleTemplateSelect('scrape')}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                      selectedTemplate === 'scrape'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50 hover:bg-accent'
                    }`}
                  >
                    <Camera className={`h-6 w-6 ${selectedTemplate === 'scrape' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className="font-medium text-xs">Scrape</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTemplateSelect('run_diff')}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                      selectedTemplate === 'run_diff'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50 hover:bg-accent'
                    }`}
                  >
                    <GitCompare className={`h-6 w-6 ${selectedTemplate === 'run_diff' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className="font-medium text-xs">Diff</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTemplateSelect('run_extract')}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                      selectedTemplate === 'run_extract'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50 hover:bg-accent'
                    }`}
                  >
                    <FileSearch className={`h-6 w-6 ${selectedTemplate === 'run_extract' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className="font-medium text-xs">Extract</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTemplateSelect('fix_diff')}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                      selectedTemplate === 'fix_diff'
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-border hover:border-amber-500/50 hover:bg-accent'
                    }`}
                  >
                    <Wrench className={`h-6 w-6 ${selectedTemplate === 'fix_diff' ? 'text-amber-600' : 'text-muted-foreground'}`} />
                    <span className="font-medium text-xs">Fix</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTemplateSelect('run_add')}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                      selectedTemplate === 'run_add'
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-border hover:border-emerald-500/50 hover:bg-accent'
                    }`}
                  >
                    <PlusCircle className={`h-6 w-6 ${selectedTemplate === 'run_add' ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                    <span className="font-medium text-xs">Add</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTemplateSelect('run_add_page')}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                      selectedTemplate === 'run_add_page'
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-border hover:border-teal-500/50 hover:bg-accent'
                    }`}
                  >
                    <FilePlus className={`h-6 w-6 ${selectedTemplate === 'run_add_page' ? 'text-teal-600' : 'text-muted-foreground'}`} />
                    <span className="font-medium text-xs">Add Pg</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTemplateSelect('tag_page')}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                      selectedTemplate === 'tag_page'
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-border hover:border-purple-500/50 hover:bg-accent'
                    }`}
                  >
                    <Tag className={`h-6 w-6 ${selectedTemplate === 'tag_page' ? 'text-purple-600' : 'text-muted-foreground'}`} />
                    <span className="font-medium text-xs">Tag</span>
                  </button>
                </div>
              </div>

            {/* Company Slug Input (for non-run_add and non-tag_page templates) */}
            {selectedTemplate && selectedTemplate !== 'run_add' && selectedTemplate !== 'run_add_page' && selectedTemplate !== 'tag_page' && (
              <div className="grid gap-1.5">
                <Label htmlFor="slug">Company Slug</Label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="slug"
                      value={slug}
                      onChange={(e) => handleSlugSearch(e.target.value)}
                      onFocus={() => {
                        if (slug.trim().length > 0 && slugSearchResults.length > 0) {
                          setShowSlugDropdown(true);
                        }
                      }}
                      placeholder={
                        (selectedTemplate === 'scrape' && bulkAllPagesScrape)
                          ? 'Bulk mode: All pages will be scraped'
                          : (selectedTemplate === 'run_extract' && bulkAllPagesExtract)
                          ? 'Bulk mode: All pages will be extracted'
                          : (selectedTemplate === 'run_diff' && bulkAllPagesDiff)
                          ? 'Bulk mode: All pages will have diffs run'
                          : 'Search by company slug...'
                      }
                      autoFocus
                      disabled={(selectedTemplate === 'scrape' && bulkAllPagesScrape) || (selectedTemplate === 'run_extract' && bulkAllPagesExtract) || (selectedTemplate === 'run_diff' && bulkAllPagesDiff)}
                      className="pl-9"
                    />
                    {slug && (
                      <button
                        type="button"
                        onClick={() => {
                          setSlug('');
                          setSlugSearchResults([]);
                          setShowSlugDropdown(false);
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Company Search Dropdown */}
                  {showSlugDropdown && slugSearchResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-input rounded-md shadow-md z-50">
                      <div className="max-h-60 overflow-y-auto">
                        {slugSearchResults.map((company) => (
                          <button
                            key={company.id}
                            type="button"
                            onClick={() => handleSlugSelect(company.slug)}
                            className="w-full text-left px-3 py-2 hover:bg-accent transition-colors flex items-center gap-2"
                          >
                            {company.logo_url && (
                              <img
                                src={company.logo_url}
                                alt={company.slug}
                                className="h-5 w-5 object-contain"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{company.slug}</div>
                              {company.category && (
                                <div className="text-xs text-muted-foreground truncate">
                                  {company.category}
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No Results Message */}
                  {showSlugDropdown && slug.trim().length > 0 && isSearchingSlug && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-input rounded-md shadow-md z-50 px-3 py-2 text-sm text-muted-foreground">
                      Searching...
                    </div>
                  )}

                  {showSlugDropdown && slug.trim().length > 0 && !isSearchingSlug && slugSearchResults.length === 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-input rounded-md shadow-md z-50 px-3 py-2 text-sm text-muted-foreground">
                      No companies found matching "{slug}"
                    </div>
                  )}
                </div>

                {selectedTemplate === 'scrape' && (
                  <div className="flex items-center gap-2 pt-1">
                    <Checkbox
                      id="bulkAllPagesScrape"
                      checked={bulkAllPagesScrape}
                      onCheckedChange={(checked) => setBulkAllPagesScrape(checked === true)}
                    />
                    <Label htmlFor="bulkAllPagesScrape" className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                      Scrape All Pages
                    </Label>
                  </div>
                )}

                {selectedTemplate === 'run_extract' && (
                  <div className="flex items-center gap-2 pt-1">
                    <Checkbox
                      id="bulkAllPagesExtract"
                      checked={bulkAllPagesExtract}
                      onCheckedChange={(checked) => setBulkAllPagesExtract(checked === true)}
                    />
                    <Label htmlFor="bulkAllPagesExtract" className="flex items-center gap-1.5 cursor-pointer text-sm">
                      <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                      Extract All Pages
                    </Label>
                  </div>
                )}

                {selectedTemplate === 'run_diff' && (
                  <>
                    <div className="flex items-center gap-2 pt-1">
                      <Checkbox
                        id="bulkAllPagesDiff"
                        checked={bulkAllPagesDiff}
                        onCheckedChange={(checked) => setBulkAllPagesDiff(checked === true)}
                      />
                      <Label htmlFor="bulkAllPagesDiff" className="flex items-center gap-1.5 cursor-pointer text-sm">
                        <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                        Diff All Pages
                      </Label>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <Checkbox
                        id="vault"
                        checked={vault}
                        onCheckedChange={(checked) => setVault(checked === true)}
                      />
                      <Label htmlFor="vault" className="flex items-center gap-1.5 cursor-pointer text-sm">
                        Vault Diff
                      </Label>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Run Add Fields */}
            {selectedTemplate === 'run_add' && (
              <div className="grid gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="pricingPageUrl">Pricing Page URL *</Label>
                  <Input
                    id="pricingPageUrl"
                    value={pricingPageUrl}
                    onChange={(e) => setPricingPageUrl(e.target.value)}
                    placeholder="https://example.com/pricing"
                    type="url"
                    autoFocus
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                    <Label htmlFor="companySlug">Company Slug</Label>
                    <Input
                      id="companySlug"
                      value={companySlug}
                      onChange={(e) => setCompanySlug(e.target.value)}
                      placeholder="e.g., slack"
                    />
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="pageSlug">Page Slug</Label>
                    <Input
                      id="pageSlug"
                      value={pageSlug}
                      onChange={(e) => setPageSlug(e.target.value)}
                      placeholder="e.g., enterprise"
                    />
                  </div>
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="navigationHint">Navigation Hint <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input
                    id="navigationHint"
                    value={navigationHint}
                    onChange={(e) => setNavigationHint(e.target.value)}
                    placeholder="e.g., click on the Insomnia tab"
                  />
                  <p className="text-xs text-muted-foreground">
                    Instructions for interactive pages that require user actions before screenshotting
                  </p>
                </div>
              </div>
            )}

            {/* Run Add Page Fields */}
            {selectedTemplate === 'run_add_page' && (
              <div className="grid gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="addPageCompanySlug">Company Slug *</Label>
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="addPageCompanySlug"
                        value={slug}
                        onChange={(e) => handleSlugSearch(e.target.value)}
                        onFocus={() => {
                          if (slug.trim().length > 0 && slugSearchResults.length > 0) {
                            setShowSlugDropdown(true);
                          }
                        }}
                        placeholder="Search by company slug..."
                        autoFocus
                        className="pl-9"
                      />
                      {slug && (
                        <button
                          type="button"
                          onClick={() => {
                            setSlug('');
                            setSlugSearchResults([]);
                            setShowSlugDropdown(false);
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {/* Company Search Dropdown */}
                    {showSlugDropdown && slugSearchResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-input rounded-md shadow-md z-50">
                        <div className="max-h-60 overflow-y-auto">
                          {slugSearchResults.map((company) => (
                            <button
                              key={company.id}
                              type="button"
                              onClick={() => handleSlugSelect(company.slug)}
                              className="w-full text-left px-3 py-2 hover:bg-accent transition-colors flex items-center gap-2"
                            >
                              {company.logo_url && (
                                <img
                                  src={company.logo_url}
                                  alt={company.slug}
                                  className="h-5 w-5 object-contain"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{company.slug}</div>
                                {company.category && (
                                  <div className="text-xs text-muted-foreground truncate">
                                    {company.category}
                                  </div>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {showSlugDropdown && slug.trim().length > 0 && isSearchingSlug && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-input rounded-md shadow-md z-50 px-3 py-2 text-sm text-muted-foreground">
                        Searching...
                      </div>
                    )}

                    {showSlugDropdown && slug.trim().length > 0 && !isSearchingSlug && slugSearchResults.length === 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-input rounded-md shadow-md z-50 px-3 py-2 text-sm text-muted-foreground">
                        No companies found matching "{slug}"
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="addPagePageSlug">Page Slug *</Label>
                  <Input
                    id="addPagePageSlug"
                    value={pageSlug}
                    onChange={(e) => setPageSlug(e.target.value)}
                    placeholder="e.g., enterprise"
                  />
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="addPagePricingUrl">Pricing Page URL *</Label>
                  <Input
                    id="addPagePricingUrl"
                    value={pricingPageUrl}
                    onChange={(e) => setPricingPageUrl(e.target.value)}
                    placeholder="https://example.com/pricing"
                    type="url"
                  />
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="addPageNavigationHint">Navigation Hint <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input
                    id="addPageNavigationHint"
                    value={navigationHint}
                    onChange={(e) => setNavigationHint(e.target.value)}
                    placeholder="e.g., click on the Enterprise tab"
                  />
                  <p className="text-xs text-muted-foreground">
                    Instructions for interactive pages that require user actions before screenshotting
                  </p>
                </div>
              </div>
            )}

            {/* Prompt Input for Fix Diff */}
            {selectedTemplate === 'fix_diff' && (
              <div className="grid gap-1.5">
                <Label htmlFor="prompt">Fix Instructions</Label>
                <Textarea
                  id="prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g., Update the Pro plan price from $10 to $12"
                  rows={2}
                />
              </div>
            )}

            {/* Tag Page Fields */}
            {selectedTemplate === 'tag_page' && (
              <div className="grid gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="tagPageSlug">Company Slug</Label>
                  <Input
                    id="tagPageSlug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder={bulkAllPages ? "All pages will be tagged" : "e.g., slack, notion, figma"}
                    disabled={bulkAllPages}
                    autoFocus={!bulkAllPages}
                  />
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="tagSelect">Tag *</Label>
                  <Select value={selectedTag} onValueChange={setSelectedTag}>
                    <SelectTrigger id="tagSelect">
                      <SelectValue placeholder="Select tag" />
                    </SelectTrigger>
                    <SelectContent>
                      {appTags && appTags.length > 0 ? (
                        appTags
                          .filter((t) => t.is_active)
                          .map((t) => (
                            <SelectItem key={t.id} value={t.tag_name}>
                              {t.tag_name}
                            </SelectItem>
                          ))
                      ) : (
                        <SelectItem value="loading" disabled>
                          Loading tags...
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2 pb-1">
                  <Checkbox
                    id="bulkAllPages"
                    checked={bulkAllPages}
                    onCheckedChange={(checked) => setBulkAllPages(checked === true)}
                  />
                  <Label htmlFor="bulkAllPages" className="flex items-center gap-1.5 cursor-pointer text-sm">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                    Tag All Pages Across All Companies
                  </Label>
                </div>
              </div>
            )}

            {/* Period Selection for Run Diff, Extract, Fix Diff, and Tag Page */}
            {(selectedTemplate === 'run_diff' || selectedTemplate === 'run_extract' || selectedTemplate === 'fix_diff' || selectedTemplate === 'tag_page') && (
              <div className="grid gap-2">
                <Label>Period</Label>

                {/* Period Type Buttons */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={periodType === 'week' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePeriodTypeChange('week')}
                  >
                    <Calendar className="h-3.5 w-3.5 mr-1" />
                    Week
                  </Button>
                  <Button
                    type="button"
                    variant={periodType === 'quarter' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePeriodTypeChange('quarter')}
                  >
                    Quarter
                  </Button>
                  <Button
                    type="button"
                    variant={periodType === 'year' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePeriodTypeChange('year')}
                  >
                    Year
                  </Button>
                  <Button
                    type="button"
                    variant={periodType === 'custom' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePeriodTypeChange('custom')}
                  >
                    Custom
                  </Button>
                </div>

                {/* Period Value Selection */}
                {periodType === 'week' && (
                  <div className="flex flex-wrap gap-1.5">
                    {recentWeeks.map((week, idx) => (
                      <Badge
                        key={week}
                        variant={period === week ? 'default' : 'outline'}
                        className="cursor-pointer text-xs"
                        onClick={() => setPeriod(week)}
                      >
                        {idx === 0 && <Zap className="h-3 w-3 mr-1" />}
                        {week}
                      </Badge>
                    ))}
                  </div>
                )}

                {periodType === 'quarter' && (
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select quarter" />
                    </SelectTrigger>
                    <SelectContent>
                      {recentQuarters.map((quarter, idx) => (
                        <SelectItem key={quarter} value={quarter}>
                          {quarter} {idx === 0 && '(current)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {periodType === 'year' && (
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {recentYears.map((year, idx) => (
                        <SelectItem key={year} value={year}>
                          {year} {idx === 0 && '(current)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {periodType === 'custom' && (
                  <Input
                    value={customPeriod}
                    onChange={(e) => setCustomPeriod(e.target.value)}
                    placeholder="e.g., 2025W47, 2025Q4, or 2025Y"
                    className="h-9"
                  />
                )}
              </div>
            )}

            {/* Priority and Force in a row */}
            {selectedTemplate && (
              <div className="flex items-end gap-4">
                <div className="grid gap-1.5 flex-1">
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={priority.toString()} onValueChange={(value) => setPriority(parseInt(value))}>
                    <SelectTrigger id="priority" className="h-9">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Urgent</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="4">4</SelectItem>
                      <SelectItem value="5">5 - Default</SelectItem>
                      <SelectItem value="6">6</SelectItem>
                      <SelectItem value="7">7</SelectItem>
                      <SelectItem value="8">8</SelectItem>
                      <SelectItem value="9">9</SelectItem>
                      <SelectItem value="10">10 - Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2 pb-1">
                  <Checkbox
                    id="force"
                    checked={force}
                    onCheckedChange={(checked) => setForce(checked === true)}
                  />
                  <Label htmlFor="force" className="flex items-center gap-1.5 cursor-pointer text-sm">
                    <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
                    Force
                  </Label>
                </div>
              </div>
            )}

            {/* Summary */}
            {selectedTemplate && (slug || bulkAllPagesScrape || bulkAllPagesExtract || bulkAllPagesDiff || (selectedTemplate === 'run_add' && pricingPageUrl) || (selectedTemplate === 'run_add_page' && slug && pageSlug) || (selectedTemplate === 'tag_page' && selectedTag)) && (
              <div className="p-2.5 rounded-lg bg-muted/50 border text-xs">
                <p className="font-mono text-muted-foreground">
                  {selectedTemplate === 'scrape'
                    ? `scrape → ${bulkAllPagesScrape ? `ALL PAGES (${fetchedPagesScrape.length > 0 ? fetchedPagesScrape.length : '?'} tasks)` : slug}${force ? ' [force]' : ''} [p:${priority}]`
                    : selectedTemplate === 'run_diff'
                    ? `run_diff → ${bulkAllPagesDiff ? `ALL PAGES (${fetchedPagesDiff.length > 0 ? fetchedPagesDiff.length : '?'} tasks)` : slug} (${periodType === 'custom' ? customPeriod : period})${force ? ' [force]' : ''}${vault ? ' [vault]' : ''} [p:${priority}]`
                    : selectedTemplate === 'run_extract'
                    ? `run_extract → ${bulkAllPagesExtract ? `ALL PAGES (${fetchedPagesExtract.length > 0 ? fetchedPagesExtract.length : '?'} tasks)` : slug} (${periodType === 'custom' ? customPeriod : period})${force ? ' [force]' : ''} [p:${priority}]`
                    : selectedTemplate === 'fix_diff'
                    ? `fix_diff → ${slug} (${periodType === 'custom' ? customPeriod : period})${force ? ' [force]' : ''} [p:${priority}]`
                    : selectedTemplate === 'run_add'
                    ? `run_add → ${companySlug || '(auto)'}${pageSlug ? '.' + pageSlug : ''}${navigationHint ? ` [hint: ${navigationHint}]` : ''}${force ? ' [force]' : ''} [p:${priority}]`
                    : selectedTemplate === 'run_add_page'
                    ? `run_add_page → ${slug}.${pageSlug}${navigationHint ? ` [hint: ${navigationHint}]` : ''}${force ? ' [force]' : ''} [p:${priority}]`
                    : selectedTemplate === 'tag_page'
                    ? `tag_page → ${bulkAllPages ? `ALL PAGES (${fetchedPages.length > 0 ? fetchedPages.length : '?'} tasks)` : slug} [${selectedTag}] (${periodType === 'custom' ? customPeriod : period})${force ? ' [force]' : ''} [p:${priority}]`
                    : ''
                  }
                </p>
                {(selectedTemplate === 'run_add' || selectedTemplate === 'run_add_page') && pricingPageUrl && (
                  <p className="text-muted-foreground mt-1 truncate">
                    {pricingPageUrl.length > 70 ? pricingPageUrl.slice(0, 70) + '...' : pricingPageUrl}
                  </p>
                )}
              </div>
            )}
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setOpen(false);
                resetForm();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={
                isSubmitting ||
                isFetchingPages ||
                isFetchingPagesScrape ||
                isFetchingPagesExtract ||
                isFetchingPagesDiff ||
                !selectedTemplate ||
                (selectedTemplate !== 'run_add' && selectedTemplate !== 'run_add_page' && selectedTemplate !== 'tag_page' && selectedTemplate !== 'scrape' && selectedTemplate !== 'run_extract' && selectedTemplate !== 'run_diff' && !slug) ||
                (selectedTemplate === 'scrape' && !bulkAllPagesScrape && !slug) ||
                (selectedTemplate === 'run_extract' && !bulkAllPagesExtract && !slug) ||
                (selectedTemplate === 'run_diff' && !bulkAllPagesDiff && !slug) ||
                (selectedTemplate === 'run_add' && !pricingPageUrl) ||
                (selectedTemplate === 'run_add_page' && (!slug || !pageSlug || !pricingPageUrl)) ||
                (selectedTemplate === 'tag_page' && ((!bulkAllPages && !slug) || !selectedTag)) ||
                ((selectedTemplate === 'run_diff' || selectedTemplate === 'run_extract' || selectedTemplate === 'fix_diff' || selectedTemplate === 'tag_page') && periodType === 'custom' && !customPeriod) ||
                (selectedTemplate === 'fix_diff' && !prompt.trim())
              }
            >
              {isFetchingPages || isFetchingPagesScrape || isFetchingPagesExtract || isFetchingPagesDiff ? 'Fetching Pages...' : isSubmitting ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

      <BulkTagPagesConfirmDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        pages={fetchedPages}
        tag={selectedTag}
        period={periodType === 'custom' ? customPeriod : period}
        force={force}
        priority={priority}
        onConfirm={handleBulkTagPagesConfirm}
        isCreating={isSubmitting}
      />

      <BulkScrapeConfirmDialog
        open={showConfirmDialogScrape}
        onOpenChange={setShowConfirmDialogScrape}
        pages={fetchedPagesScrape}
        force={force}
        priority={priority}
        onConfirm={handleBulkScrapeConfirm}
        isCreating={isSubmitting}
      />

      <BulkExtractConfirmDialog
        open={showConfirmDialogExtract}
        onOpenChange={setShowConfirmDialogExtract}
        pages={fetchedPagesExtract}
        period={periodType === 'custom' ? customPeriod : period}
        force={force}
        priority={priority}
        onConfirm={handleBulkExtractConfirm}
        isCreating={isSubmitting}
      />

      <BulkDiffConfirmDialog
        open={showConfirmDialogDiff}
        onOpenChange={setShowConfirmDialogDiff}
        pages={fetchedPagesDiff}
        period={periodType === 'custom' ? customPeriod : period}
        force={force}
        vault={vault}
        priority={priority}
        onConfirm={handleBulkDiffConfirm}
        isCreating={isSubmitting}
      />
    </>
  );
}
